import { run_with_concurrent_work_confirmation } from "../../utils/concurrent_work_confirmation";
import axios from 'axios';
import { config } from '../../config';

const list_all_pages = async(url, params = {}) => {
    const items = [];
    let cursor = '';
    let firstResponse = null;

    do {
        const response = await axios.get(url, {
            params: {
                ...params,
                limit: 100,
                ...(cursor ? { cursor } : {})
            }
        });
        firstResponse = firstResponse || response;
        const payload = response?.data?.data;

        if (Array.isArray(payload)) {
            return response;
        }

        items.push(...(Array.isArray(payload?.items) ? payload.items : []));
        cursor = payload?.hasMore && payload?.nextCursor ? payload.nextCursor : '';
    } while (cursor);

    return {
        ...firstResponse,
        data: {
            ...(firstResponse?.data || {}),
            data: items
        }
    };
};

const list_board_page = (url, params = {}) => {
    return axios.get(url, {
        params: {
            ...params,
            limit: 5
        }
    });
};

const list_sass_board_offers = (params = {}) => {
    return list_all_pages(`${config.backendUrl}/sass/board`, params);
};

const list_sass_categories = () => {
    return axios.get(`${config.backendUrl}/sass/categories`);
};

const list_sass_business_options_by_id = (offerId, params = {}) => {
    return axios.get(`${config.backendUrl}/sass/${offerId}/business-options`, {
        params
    });
};

const list_sass_timeline_by_id = (offerId, cursor = '') => {
    return axios.get(`${config.backendUrl}/sass/${offerId}/timeline`, {
        params: {
            ...(cursor.length ? { cursor } : {})
        }
    });
};

const match_prospect_user_by_username = (username = '') => {
    return axios.get(`${config.backendUrl}/prospects/match-user`, {
        params: {
            username
        }
    });
};

const list_legacy_board_offers = (params = {}) => {
    return list_all_pages(`${config.backendUrl}/offers/legacy/board`, params);
};

const list_legacy_board_offer_page = (params = {}) => {
    return list_board_page(`${config.backendUrl}/offers/legacy/board`, params);
};

const list_agency_board_offers = (params = {}) => {
    return list_all_pages(`${config.backendUrl}/agency/board`, params);
};

const list_agency_board_offer_page = (params = {}) => {
    return list_board_page(`${config.backendUrl}/agency/board`, params);
};

const list_panel_board_offers = (params = {}) => {
    return list_all_pages(`${config.backendUrl}/agency/panel/board`, params);
};

const list_goals = (params = {}) => {
    return axios.get(`${config.backendUrl}/offers/goals`, {
        params
    });
};

const list_fake_offer_brands = (search = '') => {
    return axios.get(`${config.backendUrl}/offers/admin/fake-offers/brands`, {
        params: { search }
    });
};

const list_fake_offer_brand_subscriptions = (brandId = '') => {
    return axios.get(`${config.backendUrl}/offers/admin/fake-offers/brands/${encodeURIComponent(brandId)}/subscriptions`);
};

const read_fake_offer_source = (offerCode = '') => {
    return axios.get(`${config.backendUrl}/offers/admin/fake-offers/source/${encodeURIComponent(offerCode)}`);
};

const create_fake_offer = (payload = {}) => {
    return axios.post(`${config.backendUrl}/offers/admin/fake-offers`, payload);
};

const get_fake_offer_by_id = (offerId) => {
    return axios.get(`${config.backendUrl}/offers/admin/fake-offers/${encodeURIComponent(offerId)}`);
};

const update_fake_offer_by_id = (offerId, payload = {}) => {
    return axios.put(`${config.backendUrl}/offers/admin/fake-offers/${encodeURIComponent(offerId)}`, payload);
};

const delete_fake_offer_by_id = (offerId) => {
    return axios.delete(`${config.backendUrl}/offers/admin/fake-offers/${encodeURIComponent(offerId)}`);
};

const get_agency_offer_by_id = (offerId) => {
    return axios.get(`${config.backendUrl}/agency/offers/${offerId}`);
};

const create_offer_draft = (offerData = {}) => {
    return axios.post(`${config.backendUrl}/offers/draft`, offerData);
};

const create_personalized_ads_checkout_link = (payload) => {
    return axios.post(`${config.backendUrl}/offers/personalized-ads-checkout-link`, payload);
};

const get_offer_draft_by_id = (offerId) => {
    return axios.get(`${config.backendUrl}/offers/draft/${offerId}`);
};

const update_offer_draft_by_id = (offerId, offerData = {}) => {
    return axios.put(`${config.backendUrl}/offers/draft/${offerId}`, offerData);
};

const get_public_offer_by_id = (offerId) => {
    return axios.get(`${config.backendUrl}/offers/public/${offerId}`, { skipAuthRedirect: true });
};

const get_agency_public_offer_by_id = (offerId) => {
    return axios.get(`${config.backendUrl}/agency/public/${offerId}`, { skipAuthRedirect: true });
};

const create_agency_sent_offer = (offerData = {}) => {
    return axios.post(`${config.backendUrl}/agency/offers/send`, offerData);
};

const create_agency_help_offer = (offerData = {}) => {
    return axios.post(`${config.backendUrl}/agency/offers/help`, offerData);
};

const get_offer_eligibility = (offerData) => {
    return axios.post(`${config.backendUrl}/agency/offer-eligibility`, offerData).then((response) => response.data.data);
};

const submit_agency_help_estimate_by_id = (offerId, offerData = {}) => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/help-estimate`, offerData);
};

const get_agency_preoffer_session = (offerId, issueId) => {
    return axios.get(`${config.backendUrl}/agency/offers/${offerId}/preoffer-session`, {
        params: { issueId }
    });
};

const get_agency_preoffer_reviewers = (offerType) => {
    return axios.get(`${config.backendUrl}/agency/preoffer-reviewers`, {
        params: { offerType }
    });
};

const submit_agency_preoffer_by_id = (offerId, offerData = {}) => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/preoffer-submit`, offerData);
};

const retry_agency_preoffer_sync_by_id = (offerId) => {
    return axios.post(`${config.backendUrl}/agency/offers/${offerId}/preoffer-sync`);
};

const send_agency_offer_by_id = (offerId, offerData = {}) => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/send`, offerData);
};

const track_agency_offer_visit_by_id = (offerId, payload = {}) => {
    return axios.post(`${config.backendUrl}/agency/public/${offerId}/visit`, payload, { skipAuthRedirect: true });
};

const track_offer_visit_by_id = (offerId, payload = {}) => {
    return axios.post(`${config.backendUrl}/offers/public/${offerId}/visit`, payload, { skipAuthRedirect: true });
};

const init_offer_payment_by_id = (offerId, paymentType = '', offerType = '') => {
    return axios.put(`${config.backendUrl}/offers/${offerId}/payment-init`, {
        paymentType,
        offerType
    });
};

const init_agency_offer_payment_by_id = (offerId, paymentType = '', offerType = '') => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/payment-init`, {
        paymentType,
        offerType
    });
};

const confirm_offer_payment_by_id = (offerId, paymentType = '', offerType = '', confirmationData = {}) => {
    return axios.put(`${config.backendUrl}/offers/${offerId}/payment-confirm`, {
        ...confirmationData,
        paymentType,
        offerType
    });
};

const confirm_agency_offer_payment_by_id = (offerId, paymentType = '', offerType = '', confirmationData = {}) => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/payment-confirm`, {
        ...confirmationData,
        paymentType,
        offerType
    });
};

const extend_offer_validity_by_id = (offerId, payload = {}) => {
    return axios.put(`${config.backendUrl}/offers/${offerId}/extend-validity`, payload);
};

const extend_agency_offer_validity_by_id = (offerId, payload = {}) => {
    return axios.put(`${config.backendUrl}/agency/offers/${offerId}/extend-validity`, payload);
};

const delete_offer_by_id = (offerId) => {
    return axios.delete(`${config.backendUrl}/offers/${offerId}`);
};

const delete_agency_offer_by_id = (offerId) => {
    return axios.delete(`${config.backendUrl}/agency/offers/${offerId}`);
};

const update_sass_board_status_by_id = (offerId, payload = {}) => {
    return run_with_concurrent_work_confirmation(confirmation => {
        return axios.put(`${config.backendUrl}/sass/${offerId}/board-status`, confirmation
            ? { ...payload, concurrentWorkConfirmation: confirmation } : payload);
    });
};

const update_sass_comment_by_id = (offerId, payload = {}) => {
    return axios.put(`${config.backendUrl}/sass/${offerId}/comment`, payload);
};

export {
    list_sass_board_offers,
    list_sass_categories,
    list_sass_business_options_by_id,
    list_sass_timeline_by_id,
    match_prospect_user_by_username,
    list_legacy_board_offers,
    list_legacy_board_offer_page,
    list_agency_board_offers,
    list_agency_board_offer_page,
    list_panel_board_offers,
    list_goals,
    list_fake_offer_brands,
    list_fake_offer_brand_subscriptions,
    read_fake_offer_source,
    create_fake_offer,
    get_fake_offer_by_id,
    update_fake_offer_by_id,
    delete_fake_offer_by_id,
    get_agency_offer_by_id,
    create_offer_draft,
    create_personalized_ads_checkout_link,
    get_offer_draft_by_id,
    update_offer_draft_by_id,
    get_public_offer_by_id,
    get_agency_public_offer_by_id,
    create_agency_sent_offer,
    create_agency_help_offer,
    get_offer_eligibility,
    submit_agency_help_estimate_by_id,
    get_agency_preoffer_session,
    get_agency_preoffer_reviewers,
    submit_agency_preoffer_by_id,
    retry_agency_preoffer_sync_by_id,
    send_agency_offer_by_id,
    track_offer_visit_by_id,
    track_agency_offer_visit_by_id,
    init_offer_payment_by_id,
    init_agency_offer_payment_by_id,
    confirm_offer_payment_by_id,
    confirm_agency_offer_payment_by_id,
    extend_offer_validity_by_id,
    extend_agency_offer_validity_by_id,
    delete_offer_by_id,
    delete_agency_offer_by_id,
    update_sass_board_status_by_id,
    update_sass_comment_by_id
};

export const get_offer_creation_context = (payload) => axios.post(`${config.backendUrl}/offers/creation-context`, payload);
export const confirm_offer_replacement = (id, confirmation) => axios.post(`${config.backendUrl}/offers/internal-action/${encodeURIComponent(id)}/replacement-confirmation`, { confirmation });
